/** @jsxImportSource @emotion/react */
"use client";

import type { CSSObject } from "@emotion/react";
import { ClassNames } from "@emotion/react";
import { useLocale } from "@i18n/react";
import type { Locale } from "date-fns";
import { format } from "date-fns";
import { de, enUS, es, fr, it, pl, pt } from "date-fns/locale";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DayPickerLocale } from "react-day-picker";
import {
  DayFlag,
  DayPicker,
  getDefaultClassNames,
  SelectionState,
  UI,
} from "react-day-picker";

import type { ThemeType } from "../../../../../global/emotion";
import {
  fontSizes,
  fontWeights,
  lineHeights,
  numericPixels,
  radius,
  spacing,
} from "../../../../../global/emotion";
import { cn } from "../../../../utils/cn";
import { MonthSelector } from "./MonthSelector.emotion";

const CELL_SIZE = 48;
const CALENDAR_ROWS = 6;

const cssMonthGrid = (spanCaptionRow: boolean): CSSObject => ({
  marginInline: "auto",
  minHeight: CELL_SIZE * CALENDAR_ROWS,
  // Stated rather than left to the user-agent default, which happens to be the
  // same 2px, so the gap between day cells is the design's and not the browser's.
  borderSpacing: numericPixels[2],
  // In caption mode the month is a grid whose first row is the caption, so the
  // day grid has to span every column of it.
  ...(spanCaptionRow ? { gridColumn: "1 / -1" } : {}),
});

const cssMonthCaption: CSSObject = {
  display: "none",
};

/**
 * With `navLayout="around"` react-day-picker renders the previous button, the
 * caption and the next button as direct children of the month, ahead of the day
 * grid. Laying the month out as a grid puts those three on one row and leaves
 * the day grid on the row below.
 */
const cssCaptionMonth: CSSObject = {
  display: "grid",
  gridTemplateColumns: "auto 1fr auto",
  alignItems: "center",
};

const cssCaptionMonthCaption: CSSObject = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: numericPixels[48],
};

const cssCaptionLabel = (theme: ThemeType): CSSObject => ({
  fontSize: fontSizes.titleS,
  fontWeight: fontWeights.titleS,
  lineHeight: lineHeights.titleS,
  color: theme.semanticColors.primary,
  textAlign: "center",
});

const cssMonthNavButton = (theme: ThemeType): CSSObject => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: numericPixels[48],
  height: numericPixels[48],
  padding: spacing.none,
  border: "none",
  borderRadius: radius.full,
  background: "transparent",
  color: theme.semanticColors.neutralsMedium,
  cursor: "pointer",
  "&:hover:not([aria-disabled='true'])": {
    background: theme.semanticColors.neutralsVeryLow,
    color: theme.semanticColors.primary,
  },
  "&[aria-disabled='true']": {
    opacity: 0.4,
    cursor: "not-allowed",
  },
  "&:focus-visible": {
    outline: `2px solid ${theme.semanticColors.accentMedium}`,
    outlineOffset: -2,
  },
});

const cssChevron: CSSObject = {
  width: numericPixels[20],
  height: numericPixels[20],
  fill: "currentColor",
};

const cssWeekday = (theme: ThemeType): CSSObject => ({
  width: numericPixels[48],
  height: numericPixels[48],
  fontSize: numericPixels[14],
  fontWeight: 700,
  lineHeight: numericPixels[18],
  color: theme.semanticColors.neutralsMedium,
  textAlign: "center",
  verticalAlign: "middle",
});

const cssDay: CSSObject = {
  width: numericPixels[48],
  height: numericPixels[48],
  padding: 0,
  borderRadius: radius.m,
};

const cssDayButton = (theme: ThemeType): CSSObject => ({
  position: "relative",
  zIndex: 1,
  width: numericPixels[48],
  height: numericPixels[48],
  borderRadius: radius.m,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: numericPixels[14],
  fontWeight: 700,
  lineHeight: numericPixels[18],
  color: "inherit",
  "&:disabled": {
    cursor: "not-allowed",
  },
  "&:focus-visible": {
    outline: `2px solid ${theme.semanticColors.accentMedium}`,
    outlineOffset: -2,
  },
  ".rdp-today.rdp-hidden &": {
    background: "transparent",
    color: "inherit",
  },
  ".rdp-today &:hover:not(:disabled)": {
    background: "transparent",
    color: "white",
  },
  ".rdp-selected &:hover:not(:disabled)": {
    background: "transparent",
    color: theme.semanticColors.secondary,
  },
  "&:hover:not(:disabled)": {
    background: theme.semanticColors.neutralsVeryLow,
    borderRadius: radius.m,
    color: theme.semanticColors.primary,
  },
  ".rdp-selected.rdp-today &": {
    color: theme.constantSemanticColors.onColorHighest,
  },
  ".rdp-selected.rdp-today &:hover:not(:disabled)": {
    background: "transparent",
    color: theme.constantSemanticColors.onColorHighest,
  },
});

const cssSelected = (theme: ThemeType): CSSObject => ({
  background: theme.semanticColors.primary,
  color: theme.semanticColors.secondary,
  borderRadius: radius.m,
  "&.rdp-hidden": {
    background: "transparent",
  },
});

const cssToday = (theme: ThemeType): CSSObject => ({
  color: "white",
  position: "relative",
  background: "transparent",
  "::before": {
    content: '""',
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "32px",
    height: "32px",
    borderRadius: radius.full,
    background: theme.semanticColors.accentMedium,
    zIndex: 0,
  },
  "&.rdp-hidden::before": {
    display: "none",
  },
});

const cssHighlighted = (theme: ThemeType): CSSObject => ({
  background: theme.semanticColors.accentLowest,
  color: theme.semanticColors.accentMedium,
  "&.rdp-today": {
    background: theme.semanticColors.accentLowest,
    color: "white",
  },
  "&.rdp-selected": {
    background: theme.semanticColors.primary,
    color: theme.semanticColors.secondary,
  },
  "&.rdp-hidden": {
    background: "transparent",
  },
});

const cssHasActivity = (theme: ThemeType): CSSObject => ({
  position: "relative",
  "::after": {
    content: '""',
    position: "absolute",
    bottom: 4,
    left: "50%",
    transform: "translateX(-50%)",
    width: 4,
    height: 4,
    borderRadius: "50%",
    background: theme.semanticColors.accentMedium,
    zIndex: 1,
  },
});

const cssRangeMiddle = (theme: ThemeType): CSSObject => ({
  background: theme.semanticColors.neutralsVeryLow,
  borderRadius: 0,
  "&.rdp-hidden": { background: "transparent" },
});

const cssDisabled = (): CSSObject => ({
  opacity: 0.4,
  cursor: "not-allowed",
});

const localeCodeToDateFns: Record<string, Locale> = {
  de,
  en: enUS,
  "en-PH": enUS,
  es,
  fr,
  it,
  pl,
  pt,
};

/**
 * How the user moves between months, and what each mode allows.
 *
 * `"strip"` (the default) keeps the horizontally scrolling strip of month pills,
 * which is the only thing `showYearSelector` adds a year picker to. `"caption"`
 * replaces the strip with react-day-picker's own centred month caption flanked
 * by prev/next chevrons, which has nowhere to put that year picker — so the two
 * are mutually exclusive here rather than `showYearSelector` silently doing
 * nothing. If `"caption"` ever needs a year picker, react-day-picker offers
 * `captionLayout="dropdown-years"`.
 */
type MonthNavigationProps =
  | { monthNavigation?: "strip"; showYearSelector?: boolean }
  | { monthNavigation: "caption"; showYearSelector?: never };

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  options?: {
    displayHighlighted?: (date: Date) => boolean;
    displayActivity?: (date: Date) => boolean;
  };
} & MonthNavigationProps;

export const Calendar = ({
  options,
  className,
  month: monthProp,
  onMonthChange: onMonthChangeProp,
  showYearSelector = false,
  monthNavigation = "strip",
  ...props
}: CalendarProps) => {
  const [month, setMonth] = useState(
    monthProp ?? props.defaultMonth ?? new Date(),
  );
  const isCaptionNavigation = monthNavigation === "caption";
  const defaultClassNames = getDefaultClassNames();
  const localeCode = useLocale();
  const dayPickerLocale =
    localeCodeToDateFns[localeCode] ?? (enUS as unknown as DayPickerLocale);

  // Tracks navigations we initiated so URL echoes of those don't stutter the month backwards.
  const lastMonthPropRef = useRef(
    monthProp ? format(monthProp, "yyyy-MM") : null,
  );
  const pendingMonthsRef = useRef(new Set<string>());

  useEffect(() => {
    if (!monthProp) return;
    const key = format(monthProp, "yyyy-MM");
    if (key === lastMonthPropRef.current) return;
    lastMonthPropRef.current = key;
    if (pendingMonthsRef.current.has(key)) {
      pendingMonthsRef.current.delete(key);
      return;
    }
    setMonth(monthProp);
  }, [monthProp]);

  const handleMonthChange = useCallback(
    (m: Date) => {
      const key = format(m, "yyyy-MM");
      pendingMonthsRef.current.add(key);
      setTimeout(() => pendingMonthsRef.current.delete(key), 2000);
      setMonth(m);
      onMonthChangeProp?.(m);
    },
    [onMonthChangeProp],
  );

  // `formatWeekdayName`: English's default two-letter header ("Mo") isn't the
  // single letter the design asks for. Other locales already abbreviate to one
  // or two characters, so they keep react-day-picker's own weekday formatter.
  // `formatCaption`: `"caption"` mode's design abbreviates the month
  // ("Feb 2026") where react-day-picker spells it out ("February 2026").
  const formatters = useMemo(() => {
    const isEnglish = ["en", "en-PH"].includes(localeCode);

    return {
      ...(isEnglish
        ? {
            formatWeekdayName: (date: Date) =>
              format(date, "EEEEE", { locale: enUS }),
          }
        : {}),
      ...(isCaptionNavigation
        ? {
            // `LLL` is the standalone month, mirroring the standalone `LLLL`
            // react-day-picker uses for the caption this replaces.
            formatCaption: (date: Date) =>
              format(date, "LLL y", { locale: dayPickerLocale }),
          }
        : {}),
    };
  }, [localeCode, isCaptionNavigation, dayPickerLocale]);

  const highlightMatcher = options?.displayHighlighted;
  const activityMatcher = options?.displayActivity;
  const modifiers = useMemo(
    () => ({
      ...(highlightMatcher ? { highlighted: highlightMatcher } : {}),
      ...(activityMatcher ? { hasActivity: activityMatcher } : {}),
    }),
    [highlightMatcher, activityMatcher],
  );

  return (
    <ClassNames>
      {({ css, theme }) => {
        const t = theme as ThemeType;
        const dayButton = cssDayButton(t);

        return (
          <div className={className}>
            {!isCaptionNavigation && (
              <MonthSelector
                currentMonth={month}
                onMonthChange={handleMonthChange}
                showYearSelector={showYearSelector}
              />
            )}
            <DayPicker
              {...props}
              month={month}
              onMonthChange={handleMonthChange}
              {...(isCaptionNavigation && { navLayout: "around" as const })}
              {...(Object.keys(formatters).length > 0 && { formatters })}
              locale={dayPickerLocale}
              modifiers={modifiers}
              modifiersClassNames={{
                highlighted: css(cssHighlighted(t)),
                ...(activityMatcher
                  ? { hasActivity: css(cssHasActivity(t)) }
                  : {}),
              }}
              classNames={{
                ...(isCaptionNavigation
                  ? {
                      [UI.Month]: css(cssCaptionMonth),
                      [UI.MonthCaption]: css(cssCaptionMonthCaption),
                      [UI.CaptionLabel]: css(cssCaptionLabel(t)),
                      [UI.PreviousMonthButton]: css(cssMonthNavButton(t)),
                      [UI.NextMonthButton]: css(cssMonthNavButton(t)),
                      [UI.Chevron]: css(cssChevron),
                    }
                  : { [UI.MonthCaption]: css(cssMonthCaption) }),
                [UI.MonthGrid]: css(cssMonthGrid(isCaptionNavigation)),
                [UI.Weekday]: css(cssWeekday(t)),
                [UI.Day]: cn(css(cssDay), defaultClassNames[UI.Day]),
                [UI.DayButton]: cn(
                  css(dayButton),
                  defaultClassNames[UI.DayButton],
                ),
                [DayFlag.today]: cn(
                  css(cssToday(t)),
                  defaultClassNames[DayFlag.today],
                ),
                [DayFlag.disabled]: cn(
                  css(cssDisabled()),
                  defaultClassNames[DayFlag.disabled],
                ),
                [SelectionState.selected]: cn(
                  css(cssSelected(t)),
                  defaultClassNames[SelectionState.selected],
                ),
                [SelectionState.range_middle]: cn(
                  css(cssRangeMiddle(t)),
                  defaultClassNames[SelectionState.range_middle],
                ),
              }}
              components={{
                Nav: () => <></>,
              }}
            />
          </div>
        );
      }}
    </ClassNames>
  );
};
